import { Link } from "@/i18n/LocaleLink";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMAGES, useContent } from "@/data/content";
import { useSeo } from "@/hooks/useSeo";

export default function About() {
  const { ui } = useContent();
  const a = ui.about;
  useSeo({ ...ui.seo.about, image: `/irida.jpg` });

  return (
    <main data-testid="about-page">
      <PageHero eyebrow={a.heroEyebrow} title={a.heroTitle} image={IMAGES.irida} testid="about-hero" />

      <section className="bg-cream py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{a.title}</h2>
            {a.paras.map((p, i) => (
              <p key={i} className={`mt-6 text-base leading-relaxed text-soft ${i === 0 ? "drop-cap" : ""}`}>{p}</p>
            ))}
            {a.closing && (
              <p className="mt-12 border-l-2 border-clay/40 pl-6 font-serif italic text-2xl leading-snug text-ink" data-testid="about-closing">{a.closing}</p>
            )}
            <Link
              to="/contact"
              data-testid="about-contact-button"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
            >
              {a.cta}
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream border-t border-ink/10 py-24 lg:py-32" data-testid="about-training">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{a.training.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{a.training.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{a.training.p2}</p>
            <ul className="mt-12 border-t border-ink/10" data-testid="about-training-list">
              {a.training.items.map((t, i) => (
                <li key={t.name} data-testid={`about-training-${i}`} className="flex flex-col gap-1 border-b border-ink/10 py-5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8">
                  <span className="font-serif text-lg leading-snug text-ink">{t.name}</span>
                  <span className="shrink-0 text-sm text-soft">{t.detail}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{a.valuesTitle} <span className="italic text-clay">{a.valuesAccent}</span></h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            {a.values.map(([k, v], i) => (
              <Reveal key={k} delay={i * 0.08} className="h-full">
                <div className="h-full bg-cream p-8" data-testid={`value-${i}`}>
                  <span className="font-serif italic text-clay text-lg">0{i + 1}</span>
                  <h3 className="mt-4 font-serif text-2xl text-ink">{k}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-soft">{v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="about-meet-change">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{a.meetChange.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{a.meetChange.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{a.meetChange.p2}</p>
            <div className="mt-10 space-y-4 border-l-2 border-clay/40 pl-6" data-testid="about-meet-change-questions">
              {a.meetChange.questions.map((q) => (
                <p key={q} className="font-serif italic text-xl leading-snug text-clay">{q}</p>
              ))}
            </div>
            <p className="mt-10 text-base leading-relaxed text-soft">{a.meetChange.p3}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{a.meetChange.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="about-vision">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{a.vision.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{a.vision.p1}</p>
            <div className="mt-10 space-y-3" data-testid="about-vision-moments">
              {a.vision.moments.map((m) => (
                <p key={m} className="font-serif italic text-xl leading-snug text-ink">{m}</p>
              ))}
            </div>
            <p className="mt-10 text-base leading-relaxed text-soft">{a.vision.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{a.vision.p3}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{a.vision.p4}</p>
            <p className="mt-12 border-l-2 border-clay/40 pl-6 font-serif italic text-2xl leading-snug text-ink" data-testid="about-vision-collab">{a.vision.collab}</p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
